/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  StickyNote,
  Calendar,
  User,
  Filter,
  Search,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  Eye,
  EyeOff,
  Shield,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import type React from "react";
import { useToast } from "@/app/_module/hooks/use-toast";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useCreateLeadNote, useDeleteLeadNote, useGetLeadNotes, useUpdateLeadNote, useRevealLeadNote } from "../hooks/queries";
import type { LeadComment } from "../types/lead-with-policies-type";
import type { NoteMention } from "../types/create-lead-note";
import { MentionTextarea } from "./MentionTextarea";
import { OnDeviceDraftIcon } from "@/app/_module/components/icons/OnDeviceDraftIcon";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/app/_module/components/ui/dropdown-menu";
import { draftNotesFromLatestCall, draftNotesFromCallHistory, listRecordedCalls, estimateTranscription, MIN_CONVERSATION_SECONDS, NoNotesReason, type NotesStage, type DeviceEstimate } from "../services/localCallNotes";
import { localAiAvailability, beginLocalAiSession, NOTEIQ_DISCLAIMER } from "@/app/_module/services/localAI";
import { RichNoteEditor } from "./RichNoteEditor";
import { noteIsHtml, sanitizeNoteHtml, noteToPlainText } from "./noteHtml";
import { uploadNoteImage } from "./noteImageUpload";
import { NoteImageLightbox, noteImageFromClick } from "./NoteImageLightbox";

// Extend LeadComment with editing state
interface LeadNote extends LeadComment {
  isEditing?: boolean;
}

interface LeadNotesProps {
  leadId?: string;
  initialNotes?: LeadNote[];
  /** Lead details used to pre-fill the Client Info Sheet template. */
  lead?: ClientSheetLead;
}

interface NoteCardProps {
  note: LeadNote;
  leadId: string;
  onEdit: (noteId: string) => void;
  onSave: (noteId: string, note: string, category: string, priority: string, mentions?: NoteMention[]) => void;
  onCancel: (noteId: string) => void;
  onDelete: (note: LeadNote) => void;
}

const NOTE_CATEGORIES = [
  { value: "general", label: "General", icon: StickyNote, color: "bg-gray-100 text-gray-700" },
  { value: "follow_up", label: "Follow-up", icon: Clock, color: "bg-blue-100 text-blue-700" },
  { value: "important", label: "Important", icon: AlertCircle, color: "bg-red-100 text-red-700" },
  { value: "quote", label: "Quote", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  { value: "call", label: "Call", icon: MessageSquare, color: "bg-purple-100 text-purple-700" },
  { value: "meeting", label: "Meeting", icon: Calendar, color: "bg-orange-100 text-orange-700" },
  { value: "appointment", label: "Appointment", icon: Calendar, color: "bg-teal-100 text-teal-700" },
] as const;

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-600" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-red-100 text-red-700" },
] as const;

// Client Info Sheet — one-click fill-in template mirroring the paper form agents
// use on appointments. Dropped into a note so they can type answers inline. The
// SSN / Routing / Account fields are auto-detected + encrypted by the server DLP
// on save (hasSensitiveData → the note becomes reveal-gated), so no manual
// "sensitive" toggle is needed — filling those fields marks the note sensitive.
//
// Fields we can PRE-FILL from the lead (name/address/dob/phone/email/height-weight)
// come in populated; the rest are left blank for the agent. Passing a lead is
// optional — with none, it's the plain blank template.
const escapeHtml = (v: string) =>
  v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Format a phone as (xxx) xxx-xxxx from the last 10 digits (drops +1/country
// code). Non-US / short numbers fall back to the original string.
const formatPhoneForSheet = (phone?: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  const ten = digits.length > 10 ? digits.slice(-10) : digits;
  if (ten.length !== 10) return phone.trim();
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

interface ClientSheetLead {
  name?: string;
  first_name?: string;
  last_name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dob?: string;
  phone?: string;
  email?: string;
  height?: string;
  weight?: string;
}

const buildClientInfoSheetHtml = (lead?: ClientSheetLead): string => {
  const fullName =
    lead?.name?.trim() ||
    [lead?.first_name, lead?.last_name].filter(Boolean).join(" ").trim() ||
    "";
  const address = [
    lead?.address?.trim(),
    [lead?.city?.trim(), lead?.state?.trim()].filter(Boolean).join(", "),
    lead?.zipCode?.trim(),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const heightWeight = [lead?.height?.trim(), lead?.weight?.trim()]
    .filter(Boolean)
    .join(" / ");

  // [label, prefilled value] — value "" means the agent fills it in.
  const rows: [string, string][] = [
    ["Full Name", fullName],
    ["Address", address],
    ["Date of Birth", lead?.dob?.trim() || ""],
    ["Phone Number", formatPhoneForSheet(lead?.phone)],
    ["Email Address", lead?.email?.trim() || ""],
    ["Height / Weight", heightWeight],
    ["Driver's License #", ""],
    ["Social Security #", ""],
    ["Primary Beneficiary", ""],
    ["Contingent Beneficiary", ""],
    ["Bank Name", ""],
    ["Routing Number", ""],
    ["Account Number", ""],
    ["Effective Date", ""],
    ["Declined Through", ""],
    ["In Underwriting", ""],
    ["Approved", ""],
    ["Notes", ""],
  ];

  return (
    `<div><strong>📋 Client Info Sheet</strong></div><div><br></div>` +
    rows
      .map(
        ([label, value]) =>
          `<div><strong>${label}:</strong> ${value ? escapeHtml(value) : ""}</div>`,
      )
      .join("") +
    `<div><br></div>`
  );
};

// Stable empty array to prevent infinite re-renders
const EMPTY_NOTES: any[] = [];

const NoteCard: React.FC<NoteCardProps> = function ({
  note,
  leadId,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}) {
  const [editContent, setEditContent] = useState(note.note);
  const [editCategory, setEditCategory] = useState<LeadNote["category"]>(note.category);
  const [editPriority, setEditPriority] = useState<LeadNote["priority"]>(note.priority);
  const [editMentions, setEditMentions] = useState<NoteMention[]>([]);
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealedText, setRevealedText] = useState<string | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  // Long-note clamp: tall notes collapse to ~288px with a "Show full note"
  // toggle so one long note doesn't eat the whole list.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [bodyClamped, setBodyClamped] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (el) setBodyClamped(el.scrollHeight > 300);
  }, [note.note, isRevealed, revealedText]);
  // Editor is uncontrolled — bump this key to re-seed it (sensitive-note
  // reveal swaps the masked seed for the real content; without a remount the
  // editor keeps showing asterisks and saving would DESTROY the original).
  const [editorSeed, setEditorSeed] = useState(0);
  const editDirtyRef = useRef(false);
  const revealNoteMutation = useRevealLeadNote();
  // A sensitive note gets an editor and Save only after the reveal has loaded the
  // REAL text. Save during a slow reveal sent the masked copy and wiped a client's
  // SSN/bank numbers (2026-10-22).
  const [editSource, setEditSource] = useState<"idle" | "loading" | "ready" | "failed">("idle");
  const editSessionRef = useRef(0);
  const editLocked = !!note.hasSensitiveData && editSource !== "ready";

  const loadEditSource = () => {
    const session = editSessionRef.current;
    setEditSource("loading");
    revealNoteMutation.mutateAsync({ leadId, noteId: note.id }).then((result) => {
      if (session !== editSessionRef.current) return; // edit closed or reopened since
      const text = (result as any)?.note ?? result;
      // The server answers with the masked copy when it can't decrypt: that's a failure here.
      if (typeof text === "string" && text && text !== note.note) {
        setEditContent(text);
        editDirtyRef.current = false;
        setEditorSeed((v) => v + 1); // re-seed the editor with the REAL content
        setEditSource("ready");
      } else {
        setEditSource("failed");
      }
    }).catch(() => {
      if (session === editSessionRef.current) setEditSource("failed");
    });
  };

  useEffect(() => {
    editSessionRef.current += 1;
    if (!note.isEditing) {
      setEditSource("idle");
      return;
    }
    editDirtyRef.current = false; // fresh edit session
    if (!note.hasSensitiveData) setEditSource("ready");
    else if (leadId) loadEditSource();
    else setEditSource("failed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.isEditing]);

  const categoryInfo = NOTE_CATEGORIES.find((c) => c.value === (note.isEditing ? editCategory : note.category)) || NOTE_CATEGORIES[0];
  const priorityInfo = PRIORITY_OPTIONS.find((p) => p.value === (note.isEditing ? editPriority : note.priority)) || PRIORITY_OPTIONS[1];
  const CategoryIcon = categoryInfo.icon;

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={categoryInfo.color} variant="secondary">
              <CategoryIcon className="h-3 w-3 mr-1" />
              {categoryInfo.label}
            </Badge>
            <Badge className={priorityInfo.color} variant="secondary">
              {note.priority === "high" && <Star className="h-3 w-3 mr-1" />}
              {priorityInfo.label}
            </Badge>
            {note.hasSensitiveData && (
              <Badge className="bg-amber-100 text-amber-700" variant="secondary">
                <Shield className="h-3 w-3 mr-1" />
                Sensitive
              </Badge>
            )}
          </div>
          {!note.isEditing && (
            <div className="flex items-center gap-1">
              {note.hasSensitiveData && (
                <Button
                  className="h-8 w-8 p-0"
                  onClick={async () => {
                    if (isRevealed) {
                      setIsRevealed(false);
                    } else {
                      try {
                        const result = await revealNoteMutation.mutateAsync({ leadId, noteId: note.id });
                        setRevealedText((result as any)?.note ?? result as any);
                        setIsRevealed(true);
                      } catch {
                        // Fallback — show masked text
                      }
                    }
                  }}
                  size="sm"
                  variant="ghost"
                  disabled={revealNoteMutation.isPending}
                  title={isRevealed ? "Hide sensitive data" : "Reveal sensitive data"}
                >
                  {isRevealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
              )}
              <Button
                className="h-8 w-8 p-0"
                onClick={() => onEdit(note.id)}
                size="sm"
                variant="ghost"
              >
                <Edit3 className="h-3 w-3" />
              </Button>
              <Button
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                onClick={() => onDelete(note)}
                size="sm"
                variant="ghost"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {note.isEditing ? (
          <div className="space-y-3" data-rich-note-root>
            <div className="flex gap-4">
              <Select
                value={editCategory}
                onValueChange={(value) => setEditCategory(value as LeadNote["category"])}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <SelectItem key={category.value} value={category.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {category.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                value={editPriority}
                onValueChange={(value) => setEditPriority(value as LeadNote["priority"])}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editLocked ? (
              <>
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {editSource === "failed" ? (
                    <>
                      <Shield className="mt-0.5 h-4 w-4 shrink-0" />
                      Couldn&apos;t load the full note. Editing stays off so the masked copy can&apos;t replace the real numbers.
                    </>
                  ) : (
                    <>
                      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                      Loading the full note…
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {editSource === "failed" && (
                    <Button size="sm" onClick={loadEditSource}>
                      Try again
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onCancel(note.id)}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
            <>
            <RichNoteEditor
              key={`edit-seed-${editorSeed}`}
              initialContent={editContent}
              minHeight="max(280px, calc(100vh - 560px))"
              placeholder="Edit your note…"
              onChange={(html, _text, mentions) => {
                editDirtyRef.current = true;
                setEditContent(html);
                setEditMentions(mentions);
              }}
              onImageUpload={leadId ? (file) => uploadNoteImage(leadId, file) : undefined}
              onAutoSave={() => {
                // Click-away: save changes like the composer; no changes = just close
                if (editDirtyRef.current && editContent.trim()) {
                  onSave(note.id, editContent, editCategory, editPriority, editMentions);
                } else {
                  onCancel(note.id);
                }
              }}
            />
            <div className="flex items-center gap-2">
              <Button
                className="bg-primary hover:bg-primary/90"
                disabled={!editContent.trim()}
                onClick={() => onSave(note.id, editContent, editCategory, editPriority, editMentions)}
                size="sm"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditContent(note.note);
                  setEditCategory(note.category);
                  setEditPriority(note.priority);
                  onCancel(note.id);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
            </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
            <div ref={bodyRef} className={bodyExpanded ? undefined : "max-h-72 overflow-hidden"}>
            {(() => {
              const body = isRevealed && revealedText ? revealedText : note.note;
              return noteIsHtml(body) ? (
                <div
                  className="text-sm text-gray-900 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_hr]:my-2 [&_hr]:border-gray-200 [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-gray-200 [&_img]:my-1.5 [&_img]:cursor-zoom-in"
                  onClick={(e) => { const src = noteImageFromClick(e); if (src) setLightboxSrc(src); }}
                  // Sanitized above via strict allowlist (noteHtml.ts) — server
                  // sanitizes at write too; this guards legacy/edge content.
                  dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(body) }}
                />
              ) : (
                <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">{body}</p>
              );
            })()}
            </div>
            {bodyClamped && !bodyExpanded && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
            )}
            </div>
            {(bodyClamped || bodyExpanded) && (
              <button
                type="button"
                onClick={() => setBodyExpanded((v) => !v)}
                className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${bodyExpanded ? "rotate-180" : ""}`} />
                {bodyExpanded ? "Show less" : "Show full note"}
              </button>
            )}
            {lightboxSrc && <NoteImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-3 w-3" />
                {note.commentedByName || "Unknown User"}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                {formatDateTime(note.created_at)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const LeadNotes: React.FC<LeadNotesProps> = function ({
  leadId,
  // eslint-disable-next-line react/no-object-type-as-default-prop
  initialNotes = [],
  lead,
}) {
  const { toast } = useToast();

  // Client Info Sheet HTML, pre-filled with whatever the lead already has.
  const clientInfoSheetHtml = useMemo(() => buildClientInfoSheetHtml(lead), [lead]);

  // Fetch notes from API - only if leadId exists
  const { data: notesResponse, isLoading: notesLoading, error: notesError } = useGetLeadNotes(leadId || '');

  // Use ref to store previous notes for stable reference comparison
  const prevNotesRef = useRef<any[]>(EMPTY_NOTES);

  // Extract notes from API response with memoization to prevent creating new arrays
  // React Query's data can change reference even with same data due to refetchOnMount
  const notes = useMemo(() => {
    // While loading or no response, use empty stable reference
    if (notesLoading || !notesResponse) return EMPTY_NOTES;

    const apiNotes = Array.isArray(notesResponse)
      ? notesResponse
      : (notesResponse as any)?.data || EMPTY_NOTES;

    // If empty, return stable reference
    if (!apiNotes || apiNotes.length === 0) {
      prevNotesRef.current = EMPTY_NOTES;
      return EMPTY_NOTES;
    }

    // Map backend field names to frontend expectations
    // Backend returns 'createdAt', frontend expects 'created_at'
    const normalizedNotes = apiNotes.map((note: any) => ({
      ...note,
      created_at: note.created_at || note.createdAt || new Date().toISOString(),
    }));

    // Check if the content actually changed by comparing note IDs
    const prev = prevNotesRef.current;
    if (prev !== EMPTY_NOTES && prev.length === normalizedNotes.length) {
      const sameContent = normalizedNotes.every((note: any, idx: number) =>
        prev[idx]?.id === note.id && prev[idx]?.note === note.note
      );
      if (sameContent) {
        // Content is the same, return previous reference to prevent re-render
        return prev;
      }
    }

    // Content changed, store and return new reference
    prevNotesRef.current = normalizedNotes;
    return normalizedNotes;
  }, [notesResponse, notesLoading]);

  // For editing state, track which notes are being edited
  const [editingNoteIds, setEditingNoteIds] = useState<Set<string>>(new Set());

  // Track notes with editing state applied - only create new array if editing state changes
  const notesWithEditState = useMemo(() => {
    if (notes === EMPTY_NOTES) return EMPTY_NOTES;

    // If no notes are being edited, return notes as-is to maintain reference
    if (editingNoteIds.size === 0) return notes;

    return notes.map((note: LeadNote) => ({
      ...note,
      isEditing: editingNoteIds.has(note.id)
    }));
  }, [notes, editingNoteIds]);

  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<LeadNote | null>(null);

  // API hook for creating notes
  const { mutateAsync: createNoteMutation, isPending: isCreatingNote } = useCreateLeadNote();

  // API hook for updating notes
  const { mutateAsync: updateNoteMutation, isPending: isUpdatingNote } = useUpdateLeadNote();

  // API hook for deleting notes
  const { mutateAsync: deleteNoteMutation, isPending: isDeletingNote } = useDeleteLeadNote();

  // New note form state — restores draft from localStorage if available
  const DRAFT_KEY = leadId ? `note_draft_${leadId}` : null;
  const [newNote, setNewNote] = useState(() => {
    if (typeof window !== 'undefined' && DRAFT_KEY) {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.note?.trim()) {
            return { ...parsed, mentions: parsed.mentions || [] };
          }
        }
      } catch {}
    }
    return { note: "", category: "general", priority: "medium", mentions: [] as NoteMention[] };
  });
  const draftTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasUnsavedDraft = newNote.note.trim().length > 0;

  // ── Draft from last call (on-device) ──────────────────────────────────────
  // Whole chain runs in this tab: newest recorded call -> transcript (reused if
  // already saved) -> Gemini Nano. Nothing is uploaded, nothing is billed.
  const [aiNotesReady, setAiNotesReady] = useState(false);
  // Distinct from "not ready": this browser will never support it. Measured
  // 2026-08-20 — 13 of 104 active users are on Safari or mobile, and rendering
  // nothing left them wondering where NoteIQ went.
  const [aiUnsupported, setAiUnsupported] = useState(false);
  const [aiStage, setAiStage] = useState<NotesStage | null>(null);
  const [aiEditorSeed, setAiEditorSeed] = useState(0);
  const [recordedCallCount, setRecordedCallCount] = useState(0);
  // Two separate numbers: the NEWEST conversation drives the "last call"
  // estimate, the SUM drives "all conversations". Using one for both was wrong —
  // a 2-minute last call on a lead that also holds a 44-minute one would have
  // warned about 7 minutes of work that was never going to happen.
  const [latestCallSeconds, setLatestCallSeconds] = useState(0);
  const [totalConversationSeconds, setTotalConversationSeconds] = useState(0);
  // Pending confirmation when the job is long enough to be worth asking about.
  const [aiConfirm, setAiConfirm] = useState<{ mode: "latest" | "history"; est: DeviceEstimate } | null>(null);
  // Monotonic token: only the newest draft run may drive aiStage.
  const aiRunRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    localAiAvailability()
      .then((a) => {
        if (cancelled) return;
        setAiNotesReady(a !== "unavailable");
        setAiUnsupported(a === "unavailable");
      })
      .catch(() => !cancelled && setAiUnsupported(true));
    return () => { cancelled = true; };
  }, []);

  // Count recorded calls so we only offer "all calls" when there is more than
  // one. Cheap, and only once the on-device model is actually usable — no point
  // spending a request for a button 18% of the fleet will never see.
  useEffect(() => {
    if (!aiNotesReady || !leadId) return;
    let cancelled = false;
    listRecordedCalls(leadId)
      // Count CONVERSATIONS, not dials. 93% of recorded calls in prod are under
      // a minute — offering "All 12 calls" when eleven were 7-second rings
      // promises a history that does not exist.
      .then((calls) => {
        if (cancelled) return;
        const real = calls.filter((c) => (c.duration || 0) >= MIN_CONVERSATION_SECONDS);
        setRecordedCallCount(real.length);
        // listRecordedCalls returns newest first.
        setLatestCallSeconds(real[0]?.duration || 0);
        setTotalConversationSeconds(real.reduce((n, c) => n + (c.duration || 0), 0));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [aiNotesReady, leadId]);

  /**
   * Ask first when this will actually cost the agent something.
   *
   * NoteIQ runs on the agent's own machine — that is the feature, but it also
   * means a long call on a laptop without a GPU is minutes of fans and a warm
   * keyboard. Finding that out halfway through is not acceptable, so anything
   * over a minute of estimated work asks before it starts.
   */
  const requestDraft = useCallback(async (mode: "latest" | "history" = "latest") => {
    if (!leadId) return;
    const audioSeconds = mode === "history" ? totalConversationSeconds : latestCallSeconds;
    try {
      const est = await estimateTranscription(audioSeconds || 0);
      if (est.shouldWarn) {
        setAiConfirm({ mode, est });
        return;
      }
    } catch {
      // Estimation is a courtesy — never block the feature on it.
    }
    void handleDraftFromCall(mode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, latestCallSeconds, totalConversationSeconds]);

  const handleDraftFromCall = useCallback(async (mode: "latest" | "history" = "latest") => {
    if (!leadId) return;
    setAiConfirm(null);
    // FIRST STATEMENT, BEFORE ANY await. Chrome only permits the Gemini Nano
    // download while a user gesture is still active, and activation does not
    // survive an await — the rest of this chain fetches and transcribes for
    // minutes. Claiming the session here is what makes first-run work at all;
    // verified 2026-08-21 that doing it later throws NotAllowedError.
    //
    // ⚠️ The download OUTLIVES this run. If the draft fails early (e.g. the call
    // is over the duration cap) the model keeps downloading in the background —
    // which is fine and even useful — but its progress callback must stop
    // writing to the UI, or the button sits on "Preparing the on-device model…"
    // forever after an error that already showed its toast. Seen in testing.
    const runId = ++aiRunRef.current;
    const isCurrent = () => aiRunRef.current === runId;
    const session = beginLocalAiSession((p) => {
      if (isCurrent()) setAiStage({ stage: "drafting", detail: p });
    });
    setAiStage({ stage: "finding-call" });
    // Only the newest run may write progress — see the token comment above.
    const onStage = (st: NotesStage) => { if (isCurrent()) setAiStage(st); };
    // The CRM knows who this is — hand it over so the model never guesses.
    const leadDisplayName =
      (lead as any)?.name ||
      [(lead as any)?.firstName, (lead as any)?.lastName].filter(Boolean).join(" ") ||
      null;
    try {
      // Branch rather than narrow after the fact — `"callsUsed" in res` widens
      // to Record<...> and loses the rest of HistoryDraft.
      let html: string;
      let usedExistingTranscript: boolean;
      let historyBits = "";
      if (mode === "history") {
        const res = await draftNotesFromCallHistory(leadId, onStage, session, leadDisplayName);
        html = res.html;
        usedExistingTranscript = res.usedExistingTranscript;
        const bits = [`Used ${res.callsUsed} of ${res.callsAvailable} conversations`];
        if (res.callsWithoutTranscript > 0) bits.push(`${res.callsWithoutTranscript} left for next time`);
        if (res.callsSkippedAsTooShort > 0) bits.push(`${res.callsSkippedAsTooShort} short dials ignored`);
        historyBits = ` ${bits.join(" · ")}.`;
      } else {
        const res = await draftNotesFromLatestCall(leadId, onStage, session, leadDisplayName);
        html = res.html;
        usedExistingTranscript = res.usedExistingTranscript;
      }
      // APPEND, never replace — an agent may already have typed something, and
      // silently clobbering their own words is unforgivable in a notes box.
      setNewNote((prev: any) => ({
        ...prev,
        note: prev.note?.trim() ? `${prev.note}<div><br></div>${html}` : html,
      }));
      setAiEditorSeed((v) => v + 1); // re-seed RichNoteEditor with the new content
      setIsCreating(true);
      toast({
        title: "Draft ready",
        description: (usedExistingTranscript
          ? "Built from saved transcripts. Check it before saving."
          : "Transcribed and drafted on your device. Check it before saving.") + historyBits,
      });
    } catch (err) {
      const reason = err instanceof NoNotesReason ? err.message : "Could not draft notes from the last call.";
      toast({ title: "No draft", description: reason, variant: "destructive" });
    } finally {
      // Retiring the token stops any still-running download callback from
      // reviving the stage after this run has ended.
      aiRunRef.current++;
      setAiStage(null);
    }
  }, [leadId, toast]);

  const aiStageLabel = (s: NotesStage): string => {
    switch (s.stage) {
      case "finding-call":        return "Finding the last call…";
      case "existing-transcript": return "Checking for a saved transcript…";
      case "fetching-audio":      return "Loading the recording…";
      case "model":               return `Preparing the speech model — ${s.pct}%`;
      case "transcribing":        return `Transcribing — ${Math.round(s.processed)}s of ${Math.round(s.total)}s`;
      case "drafting":            return s.detail?.stage === "downloading"
                                            ? "Preparing the on-device model…"
                                            : "Drafting notes on your device…";
      default:                    return "Working…";
    }
  };

  // Auto-open the create form if a draft was restored
  useEffect(() => {
    if (hasUnsavedDraft && !isCreating) {
      setIsCreating(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // A persisted draft-note id without draft TEXT is stale (the draft was
  // completed or abandoned) — drop it, or the next unrelated note would
  // silently overwrite that old note instead of creating its own.
  useEffect(() => {
    if (!hasUnsavedDraft && autoSavedNoteIdRef.current) {
      autoSavedNoteIdRef.current = null;
      if (DRAFT_NOTEID_KEY) { try { localStorage.removeItem(DRAFT_NOTEID_KEY); } catch {} }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Auto-save draft to localStorage (debounced 2s after last keystroke)
  useEffect(() => {
    if (!DRAFT_KEY) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

    draftTimerRef.current = setTimeout(() => {
      if (newNote.note.trim()) {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({
            note: newNote.note,
            category: newNote.category,
            priority: newNote.priority,
          }));
        } catch {}
      } else {
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
      }
    }, 2000);

    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [newNote.note, newNote.category, newNote.priority, DRAFT_KEY]);

  // Auto-save to DB (debounced 5s after last keystroke — creates or updates a draft note)
  const autoSaveDbTimerRef = useRef<NodeJS.Timeout | null>(null);
  // The draft-note id must SURVIVE remounts/revisits or every new mount
  // creates a sibling copy of the same note (LANG SHEILA had 4 near-identical
  // notes: cache invalidation after the first auto-save remounted the
  // composer, and each later visit restored the localStorage draft with no
  // memory of which note it had already created). Persist the id alongside
  // the draft so any mount keeps UPDATING the same draft note.
  const DRAFT_NOTEID_KEY = leadId ? `note_draft_noteid_${leadId}` : null;
  const autoSavedNoteIdRef = useRef<string | null>(
    typeof window !== 'undefined' && DRAFT_NOTEID_KEY
      ? (() => { try { return localStorage.getItem(DRAFT_NOTEID_KEY); } catch { return null; } })()
      : null,
  );
  const autoSaveInFlightRef = useRef(false); // a slow create + next timer must not double-create
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    if (!leadId || !newNote.note.trim()) {
      if (autoSaveDbTimerRef.current) clearTimeout(autoSaveDbTimerRef.current);
      return;
    }
    if (autoSaveDbTimerRef.current) clearTimeout(autoSaveDbTimerRef.current);

    autoSaveDbTimerRef.current = setTimeout(async () => {
      if (autoSaveInFlightRef.current) return; // never overlap — a pending create with the next timer firing was one of the duplicate sources
      autoSaveInFlightRef.current = true;
      try {
        setAutoSaveStatus('saving');
        // Ref lost (remount/second tab)? Recover the persisted draft-note id
        // at save time so we update instead of creating a sibling.
        if (!autoSavedNoteIdRef.current && DRAFT_NOTEID_KEY) {
          try { autoSavedNoteIdRef.current = localStorage.getItem(DRAFT_NOTEID_KEY); } catch {}
        }
        if (autoSavedNoteIdRef.current) {
          // Update existing auto-saved note
          try {
            await updateNoteMutation({
              leadId,
              noteId: autoSavedNoteIdRef.current,
              params: {
                note: newNote.note.trim(),
                category: newNote.category as any,
                priority: newNote.priority as any,
              },
            });
          } catch (updateErr) {
            // Draft note gone (deleted in another tab / stale persisted id) —
            // forget it so the next cycle creates fresh instead of erroring forever.
            autoSavedNoteIdRef.current = null;
            if (DRAFT_NOTEID_KEY) { try { localStorage.removeItem(DRAFT_NOTEID_KEY); } catch {} }
            throw updateErr;
          }
        } else {
          // Create new note (first auto-save)
          const result = await createNoteMutation({
            leadId,
            params: {
              note: newNote.note.trim(),
              category: newNote.category as any,
              priority: newNote.priority as any,
              mentions: newNote.mentions.length > 0 ? newNote.mentions : undefined,
            },
          });
          // Store the created note ID so subsequent saves update instead of creating duplicates
          const noteId = (result as any)?.id || (result as any)?.data?.id || (result as any)?.data?.note?.id;
          if (noteId) {
            autoSavedNoteIdRef.current = noteId;
            // Persist across remounts/revisits — the memory-only ref was the duplicate bug
            if (DRAFT_NOTEID_KEY) { try { localStorage.setItem(DRAFT_NOTEID_KEY, noteId); } catch {} }
          }
        }
        setAutoSaveStatus('saved');
        // Deliberately KEEP the localStorage draft — text and draft-note id
        // must travel together until explicit Save/Cancel (clearDraft). If we
        // clear the text here and the user leaves before the next 2s LS tick,
        // a revisit restores nothing, the stale-id guard drops the id, and
        // re-typing the note creates a sibling duplicate.
      } catch (err) {
        // Surface the failure — silent fail used to swallow this entirely,
        // letting users (Mark on Peggy Parrish was the canary) believe the
        // note saved when the server actually 500'd. localStorage still
        // preserves the draft as a fallback for this device/browser, but
        // if they close the tab on a different machine the note is gone.
        setAutoSaveStatus('idle');
        toast({
          title: "Couldn't auto-save note",
          description: "Click Save to retry — your typed text is preserved on this device.",
          variant: "destructive",
          duration: 6000,
        });
      } finally {
        autoSaveInFlightRef.current = false;
      }
    }, 3000);

    return () => { if (autoSaveDbTimerRef.current) clearTimeout(autoSaveDbTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNote.note, newNote.category, newNote.priority, leadId]);

  // Clear draft from localStorage AND reset auto-save state after explicit save
  const clearDraft = useCallback(() => {
    if (DRAFT_KEY) {
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
    }
    if (DRAFT_NOTEID_KEY) {
      try { localStorage.removeItem(DRAFT_NOTEID_KEY); } catch {}
    }
    autoSavedNoteIdRef.current = null;
    setAutoSaveStatus('idle');
    // Cancel pending auto-save (user already saved manually)
    if (autoSaveDbTimerRef.current) {
      clearTimeout(autoSaveDbTimerRef.current);
      autoSaveDbTimerRef.current = null;
    }
  }, [DRAFT_KEY, DRAFT_NOTEID_KEY]);

  // Force-save draft to localStorage on page leave (bypasses debounce)
  // Also save on component unmount (Next.js navigation)
  const newNoteRef = useRef(newNote);
  newNoteRef.current = newNote;

  useEffect(() => {
    if (!DRAFT_KEY) return;

    const forceSaveDraft = () => {
      const current = newNoteRef.current;
      if (current.note.trim()) {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({
            note: current.note,
            category: current.category,
            priority: current.priority,
          }));
        } catch {}
      }
    };

    // Save on browser close/refresh
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      forceSaveDraft();
      if (newNoteRef.current.note.trim()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Save on visibility change (tab switch, app switch)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        forceSaveDraft();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Save on component unmount (Next.js client navigation)
    return () => {
      forceSaveDraft();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [DRAFT_KEY]);

  // Filter and search notes - memoize with stable dependencies
  const filteredNotes = useMemo(() => {
    // Early return with stable reference if empty to prevent infinite loops
    if (!notesWithEditState || notesWithEditState.length === 0) return EMPTY_NOTES;

    const filtered = notesWithEditState.filter((note: LeadNote) => {
      const matchesSearch = noteToPlainText(note.note).toLowerCase().includes(searchQuery.toLowerCase()) ||
        (note.commentedByName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
      const matchesCategory = categoryFilter === "all" || note.category === categoryFilter;
      const matchesPriority = priorityFilter === "all" || note.priority === priorityFilter;

      return matchesSearch && matchesCategory && matchesPriority;
    });

    // If all filters match (filtered length equals notes length), return notes to maintain stability
    if (filtered.length === notesWithEditState.length) return notesWithEditState;

    // If filtered is empty, return stable reference
    if (filtered.length === 0) return EMPTY_NOTES;

    return filtered;
  }, [notesWithEditState, searchQuery, categoryFilter, priorityFilter]);

  // Sort notes by creation date (newest first)
  const sortedNotes = useMemo(() => {
    // Early return with stable reference if empty to prevent infinite loops
    if (filteredNotes.length === 0) return filteredNotes;

    return [...filteredNotes].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [filteredNotes]);

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getCategoryInfo = (category: LeadNote["category"]) =>
    NOTE_CATEGORIES.find((c) => c.value === category) || NOTE_CATEGORIES[0];

  const getPriorityInfo = (priority: LeadNote["priority"]) =>
    PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[1];

  const handleCreateNote = async () => {
    if (!newNote.note.trim() || !leadId) return;

    try {
      if (autoSavedNoteIdRef.current) {
        // Note was already auto-saved to DB — just do a final update
        await updateNoteMutation({
          leadId,
          noteId: autoSavedNoteIdRef.current,
          params: {
            note: newNote.note.trim(),
            category: newNote.category as "general" | "follow_up" | "important" | "quote" | "call" | "meeting" | "appointment",
            priority: newNote.priority as "low" | "medium" | "high",
          },
        });
      } else {
        // First save — create new note
        await createNoteMutation({
          leadId,
          params: {
            note: newNote.note.trim(),
            category: newNote.category as "general" | "follow_up" | "important" | "quote" | "call" | "meeting" | "appointment",
            priority: newNote.priority as "low" | "medium" | "high",
            mentions: newNote.mentions.length > 0 ? newNote.mentions : undefined,
          },
        });
      }

      setNewNote({ note: "", category: "general", priority: "medium", mentions: [] });
      clearDraft();
      setIsCreating(false);
      toast({
        title: "Note Created",
        description: "Your note has been saved successfully.",
      });
    } catch (error) {
      console.error("Failed to create note:", error);
      toast({
        title: "Failed to Create Note",
        description: error instanceof Error ? error.message : "An error occurred while saving the note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditNote = (noteId: string) => {
    setEditingNoteIds((prev) => new Set([...prev, noteId]));
  };

  const handleSaveNote = async (noteId: string, noteContent: string, category: string, priority: string) => {
    if (!noteContent.trim() || !leadId) return;

    try {
      // Call API to update the note
      await updateNoteMutation({
        leadId,
        noteId,
        params: {
          note: noteContent.trim(),
          category,
          priority,
        },
      });

      // Remove from editing state after successful update
      setEditingNoteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(noteId);
        return newSet;
      });
      toast({
        title: "Note Updated",
        description: "Your changes have been saved.",
      });
    } catch (error) {
      console.error("Failed to update note:", error);
      toast({
        title: "Failed to Update Note",
        description: error instanceof Error ? error.message : "An error occurred while updating the note. Please try again.",
        variant: "destructive",
      });
      // Keep in editing state on error so user can retry
    }
  };

  const handleCancelEdit = (noteId: string) => {
    setEditingNoteIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(noteId);
      return newSet;
    });
  };

  const handleDeleteClick = (note: LeadNote) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete || !leadId) return;

    // Optimistically remove from editing state if it was being edited
    setEditingNoteIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(noteToDelete.id);
      return newSet;
    });

    setNoteToDelete(null);
    setDeleteDialogOpen(false);

    try {
      await deleteNoteMutation({ leadId, noteId: noteToDelete.id });
      toast({
        title: "Note Deleted",
        description: "The note has been removed.",
      });
    } catch (error) {
      console.error("Failed to delete note:", error);
      toast({
        title: "Failed to Delete Note",
        description: error instanceof Error ? error.message : "An error occurred while deleting the note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const hasNotes = notesWithEditState.length > 0;

  // Show no lead selected state
  if (!leadId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Lead Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">No lead selected</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state
  if (notesLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Lead Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading notes...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state
  if (notesError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Lead Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">Failed to load notes</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Lead Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10"
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notes..."
                value={searchQuery}
              />
            </div>
            <div className="flex gap-2">
              <Select onValueChange={setCategoryFilter} value={categoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {NOTE_CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={setPriorityFilter} value={priorityFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {PRIORITY_OPTIONS.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Create New Note */}
          <div className="space-y-4">
            {!isCreating ? (
              <Button
                className="w-full justify-start gap-2 h-12 border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-600"
                onClick={() => setIsCreating(true)}
                variant="ghost"
              >
                <Plus className="h-4 w-4" />
                Add New Note
              </Button>
            ) : (
              <Card className="border-blue-200 bg-blue-50" data-rich-note-root>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex gap-4">
                    <Select
                      value={newNote.category}
                      onValueChange={(value) =>
                        setNewNote((prev: any) => ({
                          ...prev,
                          category: value,
                        }))
                      }
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NOTE_CATEGORIES.map((category) => {
                          const Icon = category.icon;
                          return (
                            <SelectItem key={category.value} value={category.value}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {category.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Select
                      value={newNote.priority}
                      onValueChange={(value) =>
                        setNewNote((prev: any) => ({
                          ...prev,
                          priority: value,
                        }))
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((priority) => (
                          <SelectItem key={priority.value} value={priority.value}>
                            {priority.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* NoteIQ — on-device call notes. Named to match DialIQ (34
                      files) rather than a generic "AI notes"; the IQ suffix is
                      the house convention for the agent-facing tools.

                      One control only. An earlier pass had the action, a second
                      action, a BETA chip and a privacy line sitting side by side
                      above the editor, which read as clutter. Scope and the beta
                      label both live in the menu now, so the resting state is a
                      single small button. */}
                  {aiUnsupported && leadId && (
                    <p className="text-[11px] text-gray-400">
                      <span className="font-semibold text-gray-500">NoteIQ</span> drafts notes from
                      your call recordings — it needs Chrome 138+ on a desktop computer.
                    </p>
                  )}
                  {aiNotesReady && leadId && (
                    <div className="flex items-center">
                      {aiConfirm ? (
                        /* THE WARNING. NoteIQ borrows the agent's computer —
                           say so in their terms (minutes, fans, "keep this tab
                           open"), not in ours (WebGPU, WASM). */
                        <div className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                          <p className="text-xs font-semibold text-amber-900">
                            This runs on your computer, not our servers.
                          </p>
                          <p className="mt-1 text-[11px] leading-snug text-amber-900/90">
                            Expect about{" "}
                            <strong>
                              {aiConfirm.est.seconds < 90
                                ? `${aiConfirm.est.seconds} seconds`
                                : `${Math.round(aiConfirm.est.seconds / 60)} minutes`}
                            </strong>{" "}
                            of work. Keep this tab open — your computer may feel slower while it runs.
                            {aiConfirm.est.slowDevice && (
                              <span className="mt-1 block font-semibold">
                                This computer has no graphics acceleration, so it will be slow. On an
                                older machine it may not finish.
                              </span>
                            )}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[11px]"
                              onClick={() => handleDraftFromCall(aiConfirm.mode)}
                            >
                              Start
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[11px]"
                              onClick={() => setAiConfirm(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : aiStage ? (
                        <span className="inline-flex items-center gap-2 h-7 pl-2 pr-2.5 rounded-full border border-violet-200 bg-violet-50 text-[11px] font-medium text-violet-800">
                          <OnDeviceDraftIcon className="h-3.5 w-3.5 animate-pulse" />
                          {aiStageLabel(aiStage)}
                        </span>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              title="NoteIQ — drafts call notes on your device"
                              className="group inline-flex items-center gap-1.5 h-7 pl-2 pr-2 rounded-full border border-gray-200 bg-white text-xs text-gray-700 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800"
                            >
                              <OnDeviceDraftIcon className="h-4 w-4 text-violet-600" />
                              <span className="font-semibold tracking-tight">NoteIQ</span>
                              <ChevronDown className="h-3 w-3 text-gray-400 group-hover:text-violet-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-60 p-1">
                            <div className="flex items-center justify-between px-2 pt-1.5 pb-1">
                              <span className="text-[11px] font-semibold text-gray-900">Draft notes from</span>
                              <span className="rounded bg-violet-100 px-1 text-[8px] font-bold leading-[14px] text-violet-700">
                                BETA
                              </span>
                            </div>
                            <DropdownMenuItem
                              className="text-xs cursor-pointer"
                              onClick={() => requestDraft("latest")}
                            >
                              The last call
                            </DropdownMenuItem>
                            {recordedCallCount > 1 && (
                              <DropdownMenuItem
                                className="text-xs cursor-pointer"
                                onClick={() => requestDraft("history")}
                              >
                                All {recordedCallCount} conversations
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5 text-[10px] leading-snug text-gray-500">
                              Chrome 138+ on desktop only. Uses this computer to process the
                              recording — nothing is uploaded. A slower computer will take longer.
                              <span className="mt-1 block text-gray-400">{NOTEIQ_DISCLAIMER}</span>
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  )}
                  <RichNoteEditor
                    key={aiEditorSeed}
                    initialContent={newNote.note}
                    minHeight="max(280px, calc(100vh - 560px))"
                    template={{ html: clientInfoSheetHtml, title: "Insert Client Info Sheet" }}
                    onChange={(html, _text, mentions) =>
                      setNewNote((prev: any) => ({ ...prev, note: html, mentions }))
                    }
                    onImageUpload={leadId ? (file) => uploadNoteImage(leadId, file) : undefined}
                    onAutoSave={handleCreateNote}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        className="bg-primary hover:bg-primary/90"
                        disabled={!newNote.note.trim() || isCreatingNote}
                        onClick={handleCreateNote}
                        size="sm"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {isCreatingNote ? "Saving..." : "Save Note"}
                      </Button>
                      <Button
                        disabled={isCreatingNote}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsCreating(false);
                          setNewNote({ note: "", category: "general", priority: "medium", mentions: [] });
                          clearDraft();
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                    {autoSaveStatus === 'saving' && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3 text-blue-500 animate-spin" />
                        Saving...
                      </span>
                    )}
                    {autoSaveStatus === 'saved' && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Auto-saved
                      </span>
                    )}
                    {hasUnsavedDraft && autoSaveStatus === 'idle' && !isCreatingNote && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-yellow-500" />
                        Draft saved
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Empty State */}
          {!hasNotes && (
            <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-gray-50">
              <StickyNote className="h-12 w-12 text-muted-foreground mb-4" />
              <div className="text-lg font-medium text-gray-900 mb-2">
                No notes yet
              </div>
              <div className="text-sm text-muted-foreground mb-4 max-w-md">
                Start documenting important information, follow-ups, and conversations about this lead.
              </div>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => setIsCreating(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Note
              </Button>
            </div>
          )}

          {/* Notes List */}
          {!!hasNotes && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">
                  {filteredNotes.length} of {notesWithEditState.length} notes
                  {!!searchQuery && ` matching "${searchQuery}"`}
                </div>
                {filteredNotes.length !== notesWithEditState.length && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSearchQuery("");
                      setCategoryFilter("all");
                      setPriorityFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>

              {filteredNotes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center border rounded-lg bg-gray-50">
                  <Filter className="h-8 w-8 text-muted-foreground mb-3" />
                  <div className="text-sm text-muted-foreground">
                    No notes match your current filters.
                  </div>
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSearchQuery("");
                      setCategoryFilter("all");
                      setPriorityFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 min-h-[400px] max-h-[calc(100vh-270px)] overflow-y-auto">
                  {sortedNotes.map((note: LeadNote) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      leadId={leadId || ""}
                      onCancel={handleCancelEdit}
                      onDelete={handleDeleteClick}
                      onEdit={handleEditNote}
                      onSave={handleSaveNote}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog onOpenChange={setDeleteDialogOpen} open={deleteDialogOpen}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
            >
              Delete Note
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadNotes;